import { useEffect, useState, useMemo } from "react";
import { Table, Drawer, Flex, Input, DatePicker, Select, Button } from "antd";
import api from "../../api";
import { useAuth } from "../../store/context/AuthContext";
import { formatDateTime } from "../../utilities/utils";
import DetailView from "./components/detail/DetailView";
import ResolvedModal from "./components/resolve/ResolvedModal";
import RejectModal from "./components/reject/RejectModal";
import GiveLottery from "../lottery/detail/components/GiveLottery";
import Loader from "../../components/spin/Loader";
import { IconPencilPlus } from "@tabler/icons-react";
import ComplainDrawer from "./components/complain/ComplainDrawer";

const { RangePicker } = DatePicker;

export default function Feedback() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10 });
  // --- Modal ---
  const [selectedId, setSelectedId] = useState(null);
  const [resolvedOpen, setResolvedOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  // --- COMPLAINT ---
  const [showDrawer, setShowDrawer] = useState(false);

  const [filters, setFilters] = useState({
    status: undefined,
    q: "",
    from: null,
    to: null,
  });

  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const { reference } = useAuth();

  const fetchList = async (page = 1) => {
    setLoading(true);
    const params = {
      page,
      limit: meta.limit,
      status: filters.status,
      q: filters.q || undefined,
      from: filters.from ? filters.from.format("YYYY-MM-DD") : undefined,
      to: filters.to ? filters.to.format("YYYY-MM-DD") : undefined,
    };
    const res = await api.get_complaint_list(params);
    setRows(res.data.data.items || []);
    setMeta((prev) => ({
      ...prev,
      total: res.data.data.total,
      page: res.data.data.page,
      limit: res.data.data.limit,
    }));
    setLoading(false);
  };

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    const res = await api.get_complaint_detail(id);
    // console.log("detail --->", res.data);
    const payload = res.data.data;
    setDetail(payload);
    setSelectedId(payload?.complaint?.id || id);
    setDetailLoading(false);
  };

  useEffect(() => {
    fetchList(meta.page);
  }, [filters, meta.page, meta.limit]);

  const columns = useMemo(
    () => [
      {
        title: "Код",
        dataIndex: ["reference", "ticket_prefix"],
        key: "ticket_prefix",
        render: (val) => val || "-",
      },
      {
        title: "Гарчиг",
        dataIndex: "title",
        key: "title",
        render: (val) => {
          const item = (reference || []).find((e) => e.reference_key === val);
          return item?.name || val || "-";
        },
      },
      {
        title: "Утасны дугаар",
        dataIndex: "phone",
        key: "phone",
        render: (val) => val || "-",
      },
      {
        title: "Төлөв",
        dataIndex: "status",
        key: "status",
        render: (status) => {
          let color = "gray";
          let label = "";
          switch (status) {
            case "pending":
              color = "orange";
              label = "Хүлээгдэж байна";
              break;
            case "resolved":
              color = "green";
              label = "Шийдэгдсэн";
              break;
            case "rejected":
              color = "red";
              label = "Татгалзсан";
              break;
            default:
              label = status || "Тодорхойгүй";
              color = "gray";
          }
          return <span style={{ color, fontWeight: 500 }}>{label}</span>;
        },
      },
      {
        title: "Үүссэн огноо",
        dataIndex: "created_at",
        key: "created_at",
        render: (val) => formatDateTime(val) || "",
      },
    ],
    [reference]
  );

  return (
    <>
      <div className="container">
        <Flex justify="space-between">
          <Flex gap={8} wrap style={{ marginBottom: 12 }}>
            <Select
              allowClear
              placeholder="Төлөв"
              style={{ width: 160, minHeight: "40px" }}
              options={[
                { value: "pending", label: "pending" },
                { value: "resolved", label: "resolved" },
                { value: "rejected", label: "rejected" },
              ]}
              value={filters.status}
              onChange={(v) => {
                setFilters((p) => ({ ...p, status: v }));
                setMeta((p) => ({ ...p, page: 1 }));
              }}
            />

            <Input
              allowClear
              placeholder="Утас"
              style={{ width: 160, minHeight: "40px" }}
              value={filters.q}
              onChange={(e) => {
                setFilters((p) => ({ ...p, q: e.target.value }));
                setMeta((p) => ({ ...p, page: 1 }));
              }}
            />

            <RangePicker
              value={[filters.from, filters.to]}
              onChange={(vals) => {
                setFilters((p) => ({
                  ...p,
                  from: vals?.[0] || null,
                  to: vals?.[1] || null,
                }));
                setMeta((p) => ({ ...p, page: 1 }));
              }}
              allowClear
            />
          </Flex>

          <Button
            variant="dashed"
            color="danger"
            // size="large"
            style={{
              padding: "0px 20px",
              width: "fit-content",
              // alignSelf: "flex-end",
            }}
            onClick={() => setShowDrawer(true)}
          >
            <IconPencilPlus size={16} />
            Гомдол үүсгэх
          </Button>
        </Flex>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={{
            current: meta.page,
            pageSize: meta.limit,
            total: meta.total,
            onChange: (p, pageSize) =>
              setMeta((prev) => ({ ...prev, page: p, limit: pageSize })),
          }}
          onRow={(record) => ({
            onClick: () => openDetail(record.id),
            style: { cursor: "pointer" },
          })}
        />

        <Drawer
          open={detailOpen}
          width={920}
          onClose={() => setDetailOpen(false)}
          title="Гомдлын дэлгэрэнгүй"
          extra={
            detail?.complaint?.status === "pending" && (
              <Flex gap={10}>
                <GiveLottery
                  data={detail?.complaint?.reference?.lottery}
                  onSuccess={() => fetchList(meta.page)}
                />
                <Button
                  type="primary"
                  style={{ padding: "0px 20px" }}
                  onClick={() => setResolvedOpen(true)}
                  disabled={!selectedId}
                >
                  Шийдэх
                </Button>
                <Button
                  danger
                  style={{ padding: "0px 20px" }}
                  onClick={() => setRejectOpen(true)}
                  disabled={!selectedId}
                >
                  Татгалзах
                </Button>
              </Flex>
            )
          }
        >
          {detailLoading ? (
            <Loader />
          ) : detail ? (
            <DetailView data={detail} />
          ) : null}
        </Drawer>
      </div>

      {/* ---------------- RESOLVED MODAL ---------------- */}
      <ResolvedModal
        visible={resolvedOpen}
        onClose={() => setResolvedOpen(false)}
        complaintId={selectedId}
        onSuccess={() => {
          fetchList(meta.page);
          setDetailOpen(false);
        }}
      />

      {/* ---------------- REJECT MODAL ---------------- */}
      <RejectModal
        visible={rejectOpen}
        onClose={() => setRejectOpen(false)}
        complaintId={selectedId}
        onSuccess={() => {
          fetchList(meta.page);
          setDetailOpen(false);
        }}
      />

      {/* ---------------- COMPLAIN DRAWER ---------------- */}
      <ComplainDrawer
        open={showDrawer}
        onClose={() => {
          fetchList(meta.page);
          setShowDrawer(false);
        }}
      />
    </>
  );
}
